// Where the map's background tiles come from, in one place.
//
// 2026-10-15: both maps hardcoded `https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png`. That is
// OSMF's own tile service, and their usage policy names this case explicitly:
//
//   Heavy use (e.g. distributing a heavy-usage app that uses tiles from openstreetmap.org) is
//   forbidden without prior permission from the Operations Working Group.
//   We may block access, without notice, if your usage degrades the service.
//
// So a released app pointed at it is relying on a service that is entitled to cut it off silently —
// and a silent cut-off looks exactly like the CSP bug that greyed out this map for three weeks.
// The same policy also says, in as many words, "do not hardcode any URL to tile.openstreetmap.org
// as doing so will limit your ability to react if the service is disrupted or blocked". Hence this
// file: one place to change, and a provider chosen on purpose.
//
// 2026-10-15, SECOND ATTEMPT. The first moved to CARTO's CDN on the belief it needed no API key.
// It does now: every tile came back HTTP 200, content-type image/png, and 2,049 bytes of a
// watermark reading "API KEY REQUIRED". Deployed, that is what the map became.
//
// The check that would have caught it is the one not done: a tile was verified by STATUS CODE and
// not by being a map. 2KB is not a plausible size for one, and counting unique colours settles it
// in a line — 16 for the watermark against 256 for a real tile.
//
// So this is back on OSM's own servers, which is the provider whose terms have actually been read
// rather than assumed. Their policy does forbid "distributing a heavy-usage app" that points here,
// and that has to be dealt with before this app is used by many people. It is a known risk at a
// known scale, which is worth more than an unknown one: Esri's grey canvas serves real tiles with
// no key and would give the muted light/dark pair this wants, but adopting it on the same kind of
// assumption that just failed is not a trade worth making twice.
//
// What this file is for has not changed, and the policy asks for it in as many words: "do not
// hardcode any URL to tile.openstreetmap.org as doing so will limit your ability to react if the
// service is disrupted or blocked". Switching provider is one line here.
//
// BEFORE ANY WIDE RELEASE, pick a licensed provider deliberately. Stadia, MapTiler and CARTO all
// have free tiers at this scale; Stadia authenticates web clients by allow-listed domain, so no
// key need go into the bundle. Whichever is chosen, verify a tile by its CONTENT.
//
// No dark variant: OSM serves one style. Dark mode keeps its dark chrome and a light map until the
// provider question is settled properly.

const OSM = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

export const BASEMAP = {
  light: OSM,
  dark: OSM,
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  subdomains: 'abc',
  maxZoom: 19,
};

// Which of the two to draw, following the system exactly as the rest of the app's palette does.
// Returns a plain string rather than a hook so a non-React caller can use it too.
export function basemapUrlFor(isDark) {
  return isDark ? BASEMAP.dark : BASEMAP.light;
}

export function prefersDark() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (_) {
    return false; // no matchMedia (old browser, SSR, a locked-down webview) — light is the safe default
  }
}

// Subscribe to system theme changes so the tiles turn over with everything else rather than
// staying light until the next reload. Returns an unsubscribe function.
export function onColorSchemeChange(fn) {
  try {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => fn(e.matches);
    // addEventListener is the modern spelling; addListener is kept for older WebKit.
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  } catch (_) {
    return () => {};
  }
}
